import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { breathalyzerAPI, driversAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wine,
  Search,
  Loader2,
  Calendar,
  Plus,
  X,
  Eye,
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  FileSpreadsheet,
  AlertTriangle,
  Pencil,
  Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTest = {
  driver_id: '',
  test_date: new Date().toISOString().slice(0, 16),
  result: '',
  immediate_action: '',
  notes: ''
};

export const BreathalyzerPage = () => {
  const [searchParams] = useSearchParams();
  const { user, isSupervisor, isAdmin } = useAuth();
  const [records, setRecords] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [showCaptureDialog, setShowCaptureDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState(emptyTest);
  const [submitting, setSubmitting] = useState(false);
  
  // Document upload
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Follow-up form
  const [furtherAction, setFurtherAction] = useState('');
  const [actionOutcome, setActionOutcome] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [recordsRes, driversRes, statsRes] = await Promise.all([
          breathalyzerAPI.list({ month: selectedMonth || undefined, result: resultFilter || undefined }),
          driversAPI.list(),
          breathalyzerAPI.getStats({ month: selectedMonth || undefined })
        ]);
        
        setRecords(recordsRes.data || []);
        setDrivers(driversRes.data || []);
        setStats(statsRes.data);
        
        // Check if capture dialog should open
        const capture = searchParams.get('capture');
        if (capture === 'true') {
          setFormData(emptyTest);
          setShowCaptureDialog(true);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams, selectedMonth, resultFilter]);

  const handleCapture = async () => {
    if (!formData.driver_id || !formData.result) {
      toast.error('Please select a driver and result');
      return;
    }
    
    if (formData.result === 'fail' && !formData.immediate_action) {
      toast.error('Immediate action is required for failed tests');
      return;
    }
    
    try {
      setSubmitting(true);
      await breathalyzerAPI.create({
        driver_id: formData.driver_id,
        test_date: new Date(formData.test_date).toISOString(),
        result: formData.result,
        immediate_action: formData.immediate_action || null,
        notes: formData.notes || null
      }, user.id, `${user.first_name} ${user.last_name}`);
      
      toast.success(formData.result === 'fail' 
        ? 'Test recorded. Corrective action created for follow-up.' 
        : 'Test recorded successfully');
      
      setShowCaptureDialog(false);
      setFormData(emptyTest);
      
      // Refresh data
      const [recordsRes, statsRes] = await Promise.all([
        breathalyzerAPI.list({ month: selectedMonth || undefined, result: resultFilter || undefined }),
        breathalyzerAPI.getStats({ month: selectedMonth || undefined })
      ]);
      setRecords(recordsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to record test:', error);
      toast.error(error.response?.data?.detail || 'Failed to record test');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setFurtherAction(record.further_action || '');
    setActionOutcome(record.action_outcome || '');
    setShowDetailDialog(true);
  };

  const handleUpdateFollowUp = async () => {
    if (!selectedRecord) return;
    
    try {
      setSubmitting(true);
      await breathalyzerAPI.update(
        selectedRecord.id,
        {
          further_action: furtherAction || null,
          action_outcome: actionOutcome || null
        },
        user.id,
        `${user.first_name} ${user.last_name}`
      );
      
      toast.success('Follow-up updated successfully');
      
      // Refresh data
      const [recordsRes, statsRes] = await Promise.all([
        breathalyzerAPI.list({ month: selectedMonth || undefined, result: resultFilter || undefined }),
        breathalyzerAPI.getStats({ month: selectedMonth || undefined })
      ]);
      setRecords(recordsRes.data || []);
      setStats(statsRes.data);
      
      // Update selected record
      const updatedRecord = recordsRes.data.find(r => r.id === selectedRecord.id);
      if (updatedRecord) {
        setSelectedRecord(updatedRecord);
      }
    } catch (error) {
      console.error('Failed to update follow-up:', error);
      toast.error('Failed to update follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRecord || !uploadType) return;

    try {
      setUploading(true);
      await breathalyzerAPI.uploadDocument(selectedRecord.id, file, uploadType);
      toast.success('Document uploaded successfully');
      
      // Refresh record
      const res = await breathalyzerAPI.get(selectedRecord.id);
      setSelectedRecord(res.data);
      
      // Refresh list
      const recordsRes = await breathalyzerAPI.list({ month: selectedMonth || undefined, result: resultFilter || undefined });
      setRecords(recordsRes.data || []);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      setUploadType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (type) => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await breathalyzerAPI.delete(id);
      toast.success('Record deleted');
      
      const [recordsRes, statsRes] = await Promise.all([
        breathalyzerAPI.list({ month: selectedMonth || undefined, result: resultFilter || undefined }),
        breathalyzerAPI.getStats({ month: selectedMonth || undefined })
      ]);
      setRecords(recordsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete record');
    }
  };

  const filteredRecords = records.filter(r => {
    const searchLower = searchQuery.toLowerCase();
    return (r.driver_name || '').toLowerCase().includes(searchLower);
  });

  // Get driver name from ID
  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return 'Unknown';
    const fn = driver.first_name || driver.user?.first_name || '';
    const ln = driver.last_name || driver.user?.last_name || '';
    return `${fn} ${ln}`.trim() || driver.employee_number || 'Unknown';
  };

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="breathalyzer-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            BREATHALYZER SCHEDULE
          </h1>
          <p className="text-slate-500 mt-1">
            Random driver alcohol testing records
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => breathalyzerAPI.exportExcel(selectedMonth)} 
            className="gap-2"
            data-testid="export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
          {(isSupervisor || isAdmin) && (
            <Button 
              onClick={() => {
                setFormData(emptyTest);
                setShowCaptureDialog(true);
              }}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
              data-testid="record-test-btn"
            >
              <Plus className="w-4 h-4" />
              Record Test
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Wine className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{stats.total_tests}</p>
                <p className="text-sm text-slate-500">Total Tests</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600 font-mono">{stats.pass_count}</p>
                <p className="text-sm text-slate-500">Passed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600 font-mono">{stats.fail_count}</p>
                <p className="text-sm text-slate-500">Failed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                <Wine className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600 font-mono">{stats.pass_rate}%</p>
                <p className="text-sm text-slate-500">Pass Rate</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600 font-mono">{stats.pending_followups}</p>
                <p className="text-sm text-slate-500">Pending Follow-ups</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by driver name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
            data-testid="search-input"
          />
        </div>
        <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full md:w-[200px] h-12">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {monthOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={resultFilter || 'all'} onValueChange={(v) => setResultFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full md:w-[150px] h-12">
            <SelectValue placeholder="All Results" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Results</SelectItem>
            <SelectItem value="pass">Pass</SelectItem>
            <SelectItem value="fail">Fail</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Records Table */}
      {filteredRecords.length === 0 ? (
        <div className="text-center py-12">
          <Wine className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No test records found</h3>
          <p className="text-slate-500 mt-1">
            {searchQuery || selectedMonth || resultFilter ? 'Try adjusting your filters' : 'Start recording breathalyzer tests'}
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Immediate Action</TableHead>
                  <TableHead>Follow-up Status</TableHead>
                  <TableHead>Docs</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow 
                    key={record.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleViewDetail(record)}
                    data-testid={`record-row-${record.id}`}
                  >
                    <TableCell className="font-mono">
                      {record.test_date ? new Date(record.test_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="font-medium">{record.driver_name}</TableCell>
                    <TableCell>
                      <Badge className={record.result === 'pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {record.result === 'pass' ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" /> PASS</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> FAIL</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {record.immediate_action || '-'}
                    </TableCell>
                    <TableCell>
                      {record.result === 'fail' ? (
                        record.action_outcome ? (
                          <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                        )
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {record.documents?.length > 0 ? (
                        <Badge variant="outline">{record.documents.length} file(s)</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleViewDetail(record)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(isSupervisor || isAdmin) && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(record.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Capture Test Dialog */}
      <Dialog open={showCaptureDialog} onOpenChange={setShowCaptureDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              RECORD BREATHALYZER TEST
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Test Date & Time *</Label>
              <Input
                type="datetime-local"
                value={formData.test_date}
                onChange={(e) => setFormData({...formData, test_date: e.target.value})}
                data-testid="test-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Driver *</Label>
              <Select 
                value={formData.driver_id} 
                onValueChange={(v) => setFormData({...formData, driver_id: v})}
              >
                <SelectTrigger data-testid="driver-select">
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {getDriverName(driver.id)} ({driver.employee_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Test Result *</Label>
              <Select 
                value={formData.result} 
                onValueChange={(v) => setFormData({...formData, result: v})}
              >
                <SelectTrigger data-testid="result-select">
                  <SelectValue placeholder="Select result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      PASS
                    </span>
                  </SelectItem>
                  <SelectItem value="fail">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      FAIL
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {formData.result === 'fail' && (
              <div className="space-y-2 bg-red-50 p-4 rounded-lg border border-red-200">
                <Label className="text-red-700">Immediate Action Taken *</Label>
                <Textarea
                  value={formData.immediate_action}
                  onChange={(e) => setFormData({...formData, immediate_action: e.target.value})}
                  placeholder="Describe the immediate action taken (e.g., driver removed from duty, transport arranged)"
                  className="border-red-300"
                  data-testid="immediate-action-input"
                />
                <p className="text-xs text-red-600">
                  A corrective action will be automatically created requiring further follow-up.
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                data-testid="notes-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowCaptureDialog(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCapture}
                disabled={submitting}
                className={formData.result === 'fail' ? 'bg-red-600 hover:bg-red-700' : 'bg-violet-600 hover:bg-violet-700'}
                data-testid="submit-test-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Recording...
                  </>
                ) : (
                  'Record Test'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail/Follow-up Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              TEST RECORD DETAILS
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6 mt-4">
              {/* Test Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Test Date</Label>
                  <p className="font-medium">
                    {selectedRecord.test_date ? new Date(selectedRecord.test_date).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Driver</Label>
                  <p className="font-medium">{selectedRecord.driver_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Result</Label>
                  <Badge className={selectedRecord.result === 'pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {selectedRecord.result?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-500">Recorded By</Label>
                  <p className="font-medium">{selectedRecord.recorded_by_name}</p>
                </div>
              </div>
              
              {/* Immediate Action (if failed) */}
              {selectedRecord.result === 'fail' && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <Label className="text-red-700 mb-2 block">Immediate Action Taken</Label>
                  <p className="text-slate-900">{selectedRecord.immediate_action || '-'}</p>
                </div>
              )}
              
              {/* Follow-up Section (if failed) */}
              {selectedRecord.result === 'fail' && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Follow-up Actions Required
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Further Action Taken</Label>
                      <Textarea
                        value={furtherAction}
                        onChange={(e) => setFurtherAction(e.target.value)}
                        placeholder="Describe the further action taken (e.g., disciplinary hearing, counseling)"
                        data-testid="further-action-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Outcome of Action</Label>
                      <Textarea
                        value={actionOutcome}
                        onChange={(e) => setActionOutcome(e.target.value)}
                        placeholder="Describe the outcome (e.g., written warning issued, driver suspended for X days)"
                        data-testid="action-outcome-input"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleUpdateFollowUp}
                      disabled={submitting}
                      className="w-full"
                      data-testid="update-followup-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Update Follow-up
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedRecord.notes && (
                <div>
                  <Label className="text-slate-500">Notes</Label>
                  <p className="text-slate-900">{selectedRecord.notes}</p>
                </div>
              )}
              
              {/* Documents */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents & Acknowledgements
                  </h4>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleUpload}
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Upload Buttons */}
                  <Card className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Incident Report</span>
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('incident_report')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Acknowledgement</span>
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('acknowledgement')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Outcome Document</span>
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('outcome')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </Card>
                  <Card className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Other</span>
                      <Button size="sm" variant="outline" onClick={() => triggerUpload('other')} disabled={uploading}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </Card>
                </div>
                
                {uploading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Uploading...</span>
                  </div>
                )}
                
                {/* Uploaded Files List */}
                {selectedRecord.documents?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <Label className="text-slate-500">Uploaded Files</Label>
                    {selectedRecord.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">{doc.filename}</span>
                          <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                        </div>
                        <a 
                          href={`${API_URL}${doc.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BreathalyzerPage;
